import React, { useEffect, useRef, useState } from 'react';
import AtticInspection from './AtticInspection';
import QuoteBuilder from '../Sales/QuoteBuilder';
import ReportView from './ReportView';
import PhotoSlot from './PhotoSlot';
import { queueChange } from '../../services/sync';
import { CURRENT_USER } from '../../services/currentUser';
import { SHARED, deleteDraft, getSentEntry, newDraftId, refreshDrafts, saveDraft, saveSent, saveSentEdit, useDraftSync, useDrafts, useSentInspections } from '../../services/drafts';
import { addFollowUpFromReport } from '../../services/followUps';
import { LEADS_SOURCE, markReportSent, useLeads } from '../../services/leads';
import { submitInspection } from '../../services/inspectionSubmit';
import { QUOTES_LIVE, sendQuotes } from '../../services/quoteSend';
import { textReport } from '../../services/reportText';
import { REPORT_EMAIL_LIVE, reportRValue } from '../../services/reportEmail';
import { conversationKey } from '../../services/communications';
import NextStep from './NextStep';
import { collectPhotos } from './photos';
import { applyZohoQuotes, pullZohoQuotes, zohoConflicts } from '../../services/quoteSync';
import { totalAtticSqft } from '../../services/catalog';
import { DEMO_REPORT_TOKEN, buildInspectionReport, fmtDate, getReport, saveReport } from './reportData';
import './Inspection.css';

const PROPERTY_TYPES = ['Bungalow', 'Sidesplit', '2 Story', '3 Story', 'Townhouse', 'Semi'];

// only Attic has an inspection page so far — the rest are shown so agents know they're coming
const SERVICES = [
  { id: 'attic', name: 'Attic Insulation', description: '8 sub-sections · stacked accordion', icon: '📦' },
  { id: 'spray', name: 'Spray Foam', description: 'Area-by-area · closed-cell, R-target', icon: '💨', soon: true },
  { id: 'dense', name: 'Dense Pack (walls)', description: 'Wall area, cavity, patching', icon: '🧱', soon: true },
  { id: 'fiberglass', name: 'Fiberglass Bat Installation', description: 'Wall area, batt type, per-section photos', icon: '📋', soon: true },
  { id: 'garage', name: 'Garage Ceiling Insulation', description: 'Ceiling area, depth, method', icon: '🏠', soon: true },
  { id: 'hvac', name: 'Heating & Cooling', description: 'Furnace · AC · Ductwork', icon: '⚡', soon: true },
];

// the phone's calendar day for a saved time — slicing the ISO string gives the UTC day (tomorrow, after 8 PM)
const localDay = (iso) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const emptyDetails = (lead) => ({
  fullName: lead?.contactName || '',
  phone: lead?.servicePhone || '',
  email: lead?.email || '',
  streetAddress: lead?.serviceAddress || '',
  propertyType: 'Bungalow',
  mainConcern: '',
});

const Inspection = ({ inspection, onBack, onFinished, onOpenCommunication, initialTab = 'new' }) => {
  const drafts = useDrafts();
  const leads = useLeads();
  const sentInspections = useSentInspections();
  const draftSync = useDraftSync();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentStep, setCurrentStep] = useState(0); // 0=Details, 1=Services, 2=Attic, 3=Quote
  const [viewingReport, setViewingReport] = useState(null);
  const [sentReport, setSentReport] = useState(null);
  // after Send: Book job / Office task / Follow up must be picked before leaving
  const [nextStep, setNextStep] = useState(null);
  const [peekReport, setPeekReport] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [editChoice, setEditChoice] = useState(null); // sent inspection whose Edit menu is open
  const [toast, setToast] = useState('');

  // everything gathered for the report — owned here so Back / forward between steps keeps it
  const [draftId, setDraftId] = useState(newDraftId);
  // the Zoho appointment this inspection belongs to — quotes, findings and the PDF go there
  const [linkedLeadId, setLinkedLeadId] = useState(inspection?.id || null);
  const [formData, setFormData] = useState(() => emptyDetails(inspection));
  const [selectedServices, setSelectedServices] = useState([]);
  const [frontPhoto, setFrontPhoto] = useState(''); // becomes the report hero
  const [atticData, setAtticData] = useState(null); // { formData, atticAreas, photos }
  const [quoteState, setQuoteState] = useState(null); // { currentPage, packages }
  const [atticKey, setAtticKey] = useState(0); // remounts the attic / quote pages after a draft loads

  // opening Previous reports always pulls everyone's latest drafts
  useEffect(() => {
    if (activeTab === 'previous') refreshDrafts();
  }, [activeTab]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---------- Zoho appointment link ---------- */
  // appointments from Zoho, newest first; the linked one stays listed even if it's outside the window
  const linkableLeads = [...leads].sort((a, b) => String(b.appointmentStart || '').localeCompare(String(a.appointmentStart || '')));
  const apptLabel = (iso) =>
    iso ? new Date(iso).toLocaleString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'no time';
  const linkLead = (id) => {
    setLinkedLeadId(id || null);
    const lead = leads.find((l) => l.id === id);
    if (!lead) return;
    // fill in what's still blank from the appointment — never overwrite what the agent typed
    setFormData((prev) => ({
      ...prev,
      fullName: prev.fullName || lead.contactName || '',
      phone: prev.phone || lead.servicePhone || '',
      email: prev.email || lead.email || '',
      streetAddress: prev.streetAddress || lead.serviceAddress || '',
    }));
  };

  /* ---------- drafts: saved on the phone as the agent works ---------- */
  const snapshot = () => ({
    id: draftId,
    leadId: linkedLeadId,
    customer: formData.fullName,
    address: formData.streetAddress,
    currentStep,
    formData,
    selectedServices,
    frontPhoto,
    atticData,
    quoteState,
  });

  // only real work makes a draft — opening a lead (which pre-fills its details) doesn't
  const hasWork = !!(
    frontPhoto ||
    atticData ||
    quoteState ||
    selectedServices.length ||
    JSON.stringify(formData) !== JSON.stringify(emptyDetails(inspection))
  );
  const latest = useRef(snapshot);
  latest.current = snapshot;

  // a reopened sent inspection stays Sent: edits are saved onto it (never as a draft); just looking saves nothing
  const reopened = useRef(null); // baseline content of the sent inspection being edited, or null
  const contentKey = (snap) =>
    JSON.stringify({ ...snap, currentStep: undefined, quoteState: snap.quoteState ? { ...snap.quoteState, currentPage: undefined } : null });

  useEffect(() => {
    if (sentReport || !hasWork) return undefined;
    const t = setTimeout(() => {
      const snap = latest.current();
      if (reopened.current !== null) {
        if (reopened.current === '') reopened.current = contentKey(snap); // first look after opening = baseline
        if (contentKey(snap) === reopened.current) return; // just looking
        reopened.current = contentKey(snap);
        saveSentEdit(snap); // edited → saved onto the sent inspection, still Sent
        return;
      }
      saveDraft(snap);
    }, 800);
    return () => clearTimeout(t);
  }, [formData, selectedServices, frontPhoto, atticData, quoteState, currentStep, hasWork, sentReport]);

  /* Previous reports → a sent inspection. Same id, so a resend replaces it (and updates the same Zoho quotes).
     mode: 'view' → Review & send · 'quote' → the saved packages · 'report' → the attic inspection */
  const openSent = (entry, mode) => {
    const src = entry.source;
    if (!src) {
      setViewingReport(entry.report); // sent before inspections were saved in full — read-only report
      return;
    }
    reopened.current = '';
    const quoteState = src.quoteState || null;
    const opened = {
      ...src,
      id: entry.id,
      leadId: entry.leadId || src.leadId,
      currentStep: mode === 'report' ? 2 : 3,
      quoteState: quoteState ? { ...quoteState, currentPage: mode === 'view' ? 'review' : 'select' } : null,
    };
    loadDraft(opened);
    setEditChoice(null);
    syncFromZoho(opened);
  };

  /* Two-way sync: quotes edited in Zoho since the app last saw them replace the app's packages,
     and their PDFs are rebuilt so the attachment in Zoho matches again. */
  const syncFromZoho = async (opened) => {
    const qs = opened.quoteState;
    if (!QUOTES_LIVE || !opened.leadId || !qs || !(qs.packages || []).length) return;
    let zohoQuotes;
    try {
      zohoQuotes = await pullZohoQuotes(opened.leadId);
    } catch {
      return; // no signal — the app's copy is used
    }
    const r = applyZohoQuotes(qs.packages, qs.formats || {}, zohoQuotes);
    if (!r.updated.length && !r.stamped) return;
    const next = { ...qs, packages: r.packages, formats: r.formats };
    setQuoteState(next);
    setAtticKey((k) => k + 1); // the quote pages reload with Zoho's version
    saveSentEdit({ ...opened, quoteState: next });
    if (r.updated.length) {
      setToast(`Updated from Zoho: ${r.updated.join(', ')}`);
      const changed = r.packages.filter((p) => r.updated.includes(p.name));
      sendQuotes({
        inspectionId: opened.leadId,
        draftId: opened.id,
        packages: changed,
        formats: r.formats,
        customer: { name: opened.formData?.fullName, address: opened.formData?.streetAddress, email: opened.formData?.email, phone: opened.formData?.phone },
        agent: CURRENT_USER.name,
      });
    }
  };

  const loadDraft = (d, { asNew = false } = {}) => {
    if (asNew || d.id !== draftId) reopened.current = reopened.current === '' ? '' : null;
    setDraftId(asNew ? newDraftId() : d.id);
    setLinkedLeadId(d.leadId || inspection?.id || null); // a resumed draft keeps its Zoho appointment
    setFormData(d.formData || emptyDetails(null));
    setSelectedServices(d.selectedServices || []);
    setFrontPhoto(d.frontPhoto || '');
    setAtticData(d.atticData || null);
    setQuoteState(d.quoteState || null);
    setCurrentStep(asNew ? 0 : d.currentStep || 0);
    setAtticKey((k) => k + 1);
    setActiveTab('new');
  };

  /* Next → quote: findings + photos PDF go to the Zoho Inspection. Never holds the agent up —
     no signal just queues it, and a redo replaces what Zoho has. */
  const handleInspectionDone = async (data) => {
    const draft = { ...snapshot(), atticData: data, currentStep: 3 };
    await saveDraft(draft); // a queued send rebuilds from this saved copy
    try {
      const r = await submitInspection({ inspectionId: linkedLeadId, draftId: draft.id, customer: formData.fullName, frontPhoto, atticData: data });
      if (r.sent) setToast('Inspection saved to Zoho');
      else if (r.queued) setToast('No signal — inspection will go to Zoho when you’re back online');
    } catch (err) {
      setToast(err.message || 'Zoho didn’t save the inspection');
    }
  };

  const handleSaveDraft = async () => {
    if (reopened.current !== null) {
      saveSentEdit(snapshot());
      setToast('Saved — still sent');
      return;
    }
    await saveDraft(snapshot());
    setToast(SHARED ? 'Draft saved — any account can resume it' : 'Draft saved on this phone');
  };

  // every step has Save & exit; resuming the draft opens the same step (and quote page)
  const handleSaveExit = async () => {
    if (reopened.current !== null) {
      // a sent inspection: keep any changes on it, and it stays Sent
      if (contentKey(snapshot()) !== reopened.current && reopened.current !== '') saveSentEdit(snapshot());
      onBack();
      return;
    }
    if (hasWork) await saveDraft(snapshot());
    onBack();
  };

  // this lead already has a draft in progress
  const leadDraft = linkedLeadId ? drafts.find((d) => d.leadId === linkedLeadId && d.id !== draftId) : null;

  /* ---------- steps ---------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggleService = (serviceId) => {
    setSelectedServices((prev) => (prev.includes(serviceId) ? prev.filter((id) => id !== serviceId) : [...prev, serviceId]));
  };

  const handleBackToPrevStep = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const canStartInspection = selectedServices.includes('attic');
  const allPhotos = collectPhotos(frontPhoto, atticData);

  /* ---------- send: report + quotes go out, the lead moves on, Calls picks it up ---------- */
  const handleSent = async (quoteData) => {
    const leadId = linkedLeadId;
    // someone edited one of these quotes in Zoho while this was being edited here — ask before overwriting
    if (QUOTES_LIVE && leadId) {
      try {
        const zohoQuotes = await pullZohoQuotes(leadId);
        const conflicts = zohoConflicts(quoteData.packages, zohoQuotes);
        if (conflicts.length) {
          const overwrite = window.confirm(
            `${conflicts.map((p) => p.name).join(', ')} was changed in Zoho after you opened it.\n\n` +
              'OK — send your version (replaces the Zoho changes)\nCancel — load the Zoho version instead'
          );
          if (!overwrite) {
            const qs = quoteState || {};
            const r = applyZohoQuotes(qs.packages || [], qs.formats || {}, zohoQuotes);
            setQuoteState({ ...qs, packages: r.packages, formats: r.formats, currentPage: 'select' });
            setAtticKey((k) => k + 1);
            setToast('Loaded the Zoho version — review it, then send');
            return;
          }
        }
      } catch {
        /* no signal — send goes through the queue as usual */
      }
    }
    const report = buildInspectionReport({
      lead: inspection,
      details: formData,
      frontPhoto,
      attic: atticData,
      packages: quoteData.packages,
      formats: quoteData.formats,
      preparer: CURRENT_USER.name,
    });
    const sentSnapshot = { ...snapshot(), quoteState: { ...(quoteState || {}), formats: quoteData.formats || {} } };
    const token = saveReport(report, { leadId, source: sentSnapshot });

    // the customer's report text goes out first, before anything else is queued: the report is shared
    // (that's what makes the link open), then texted — the next-step screen waits for it
    const resend = !!(draftId && getSentEntry(draftId));
    saveSent({ id: draftId, token, report, leadId, source: sentSnapshot });
    const reportText = textReport({
      inspectionId: leadId,
      draftId,
      token,
      phone: (quoteData.phones || [])[0] || formData.phone,
      name: formData.fullName || report.header.customer,
      quotes: report.quotes.length,
      resend,
    });
    if (leadId) markReportSent(leadId, { quotes: quoteData.packages.length });

    const f = (atticData && atticData.formData) || {};
    addFollowUpFromReport({
      leadId,
      name: formData.fullName,
      address: formData.streetAddress,
      phone: formData.phone,
      email: formData.email,
      homeType: formData.propertyType,
      packages: quoteData.packages,
      photos: allPhotos,
      // internal notes travel with the follow-up, never onto the customer's report
      notes: [
        formData.mainConcern && `Main concern: ${formData.mainConcern}`,
        f.inspectorNotes && `Inspector notes: ${f.inspectorNotes}`,
        f.soffit12Notes && `Soffits: ${f.soffit12Notes}`,
        f.airSealing13Notes && `Air sealing: ${f.airSealing13Notes}`,
        f.pestNotes && `Pest: ${f.pestNotes}`,
      ],
    });

    // to Zoho: a Quote per package + the report on the Deal
    queueChange({
      type: 'inspection.sent',
      leadId,
      label: `${report.header.customer || 'Customer'} — ${quoteData.includeReport ? 'inspection report + ' : ''}${report.quotes.length} quote${report.quotes.length === 1 ? '' : 's'} sent`,
      payload: {
        emails: quoteData.emails,
        phones: quoteData.phones,
        includeReport: quoteData.includeReport,
        quotes: report.quotes.map((q) => ({ name: q.name, total: q.price })),
      },
    });
    // one Zoho Quote per package + its PDF (valid 90 days from today; a resend updates the same quotes)
    sendQuotes({
      inspectionId: leadId,
      draftId,
      packages: quoteData.packages,
      formats: quoteData.formats || {},
      customer: { name: formData.fullName, address: formData.streetAddress, email: (quoteData.emails || [])[0] || formData.email, phone: (quoteData.phones || [])[0] || formData.phone },
      agent: CURRENT_USER.name,
      // after the PDFs: the report email (R-value card + benefits + report link, PDFs attached)
      email: {
        to: (quoteData.emails || [])[0] || formData.email || '',
        name: formData.fullName || report.header.customer,
        token,
        contact: conversationKey({ servicePhone: (quoteData.phones || [])[0] || formData.phone }) || '',
        resend,
        rvalue: reportRValue(report),
      },
    });
    reopened.current = null;
    setSentReport(report);
    // the report email follows the quote PDFs — the next-step screen shows it going out (never blocks)
    const emailTo = (quoteData.emails || [])[0] || formData.email || '';
    const reportEmail = REPORT_EMAIL_LIVE && QUOTES_LIVE && leadId && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo) ? { to: emailTo, since: new Date().toISOString() } : null;
    setNextStep({ inspectionId: leadId, customerName: formData.fullName || report.header.customer, packages: quoteData.packages, reportText, reportEmail });
  };

  if (viewingReport) {
    return <ReportView report={viewingReport} onBack={() => setViewingReport(null)} />;
  }

  if (nextStep) {
    if (peekReport && sentReport) return <ReportView report={sentReport} onBack={() => setPeekReport(false)} />;
    return (
      <NextStep
        {...nextStep}
        onViewReport={sentReport ? () => setPeekReport(true) : null}
        onDone={onFinished || onBack}
      />
    );
  }

  if (sentReport) {
    return <ReportView report={sentReport} onBack={onBack} />;
  }

  /* ---------- previous reports: sent this session + drafts on this phone ---------- */
  const q = search.trim().toLowerCase();
  const matches = (name, address) => !q || `${name || ''} ${address || ''}`.toLowerCase().includes(q);
  const sent = sentInspections.filter(({ report }) =>
    matches(report.header.customer, [report.header.address, report.header.cityline].join(' '))
  );
  const draftList = drafts.filter((d) => matches(d.customer, d.address));
  const reportCount = sentInspections.length + drafts.length;

  const onQuoteStep = activeTab === 'new' && currentStep === 3;

  return (
    <div className="inspection-page">
      {/* iPhone: every header row costs screen. The quote steps bring their own header
          (its Back returns to the attic page), so this page's chrome steps aside there. */}
      {!onQuoteStep && (
        <div className="inspection-header">
          <button className="inspection-back-btn" onClick={activeTab === 'previous' || currentStep === 0 ? onBack : handleBackToPrevStep}>
            ← Back
          </button>
          <div className="inspection-header-content">
            <div className="inspection-step-label">
              {activeTab === 'previous' ? 'REPORTS' : ['DETAILS', 'SERVICES', 'INSPECTION', 'QUOTE'][currentStep]}
            </div>
            <h2>
              {activeTab === 'previous'
                ? 'Previous reports'
                : ['New inspection', 'Select services', 'Attic inspection', 'Build & send quote'][currentStep]}
            </h2>
          </div>
          {activeTab === 'new' && (
            <button type="button" className="inspection-save-exit" onClick={handleSaveExit}>
              Save & exit
            </button>
          )}
        </div>
      )}

      {/* TABS — only before the flow starts (or while browsing previous reports) */}
      {(activeTab === 'previous' || currentStep === 0) && (
        <div className="inspection-tabs">
          <button className={`inspection-tab ${activeTab === 'new' ? 'active' : ''}`} onClick={() => setActiveTab('new')}>
            New inspection
          </button>
          <button className={`inspection-tab ${activeTab === 'previous' ? 'active' : ''}`} onClick={() => setActiveTab('previous')}>
            <span className="tab-text">Previous reports</span>
            <span className="tab-count">{reportCount}</span>
          </button>
        </div>
      )}

      {/* PROGRESS — dots + step names in one row */}
      {activeTab === 'new' && !onQuoteStep && (
        <div className="inspection-steps">
          {[0, 1, 2, 3].map((step) => (
            <div key={step} className={`step-dot ${step === currentStep ? 'active' : step < currentStep ? 'completed' : ''}`} />
          ))}
          <div className="inspection-breadcrumb">
            {['Details', 'Services', 'Inspection', 'Quote'].map((name, i) => (
              <span key={name} className={i === currentStep ? 'current' : ''}>{i > 0 && ' · '}{name}</span>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'new' && currentStep === 1 && (
        <div className="inspection-step-counter">
          <span>{selectedServices.length} selected</span>
        </div>
      )}

      {/* DETAILS */}
      {activeTab === 'new' && currentStep === 0 && (
        <div className="inspection-content">
          {leadDraft && (
            <div className="draft-banner">
              <span>You have an unfinished inspection for {leadDraft.customer || 'this customer'} ({fmtDate(localDay(leadDraft.updatedAt))}).</span>
              <button onClick={() => loadDraft(leadDraft)}>Resume</button>
            </div>
          )}

          {/* ZOHO APPOINTMENT — without it, nothing from this inspection can reach Zoho */}
          {LEADS_SOURCE === 'zoho' && (
            <div className="inspection-section zoho-link">
              <div className="section-label">ZOHO APPOINTMENT</div>
              <select
                className={`zoho-link-select${linkedLeadId ? ' linked' : ''}`}
                value={linkedLeadId || ''}
                onChange={(e) => linkLead(e.target.value)}
              >
                <option value="">Not linked — pick the customer's appointment</option>
                {linkableLeads.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.contactName || 'No name'} · {apptLabel(l.appointmentStart)}
                  </option>
                ))}
              </select>
              <p className={`zoho-link-help${linkedLeadId ? '' : ' warn'}`}>
                {linkedLeadId
                  ? 'Findings, the photo PDF and quotes will be saved on this appointment in Zoho.'
                  : 'Not linked: findings and quotes won’t reach Zoho. Pick the appointment (walk-ins can stay unlinked).'}
              </p>
            </div>
          )}

          {/* FRONT OF HOME */}
          <div className="inspection-section photo-section">
            <div className="section-label">FRONT OF HOME</div>
            <div className={`photo-capture-box${frontPhoto ? ' has-photo' : ''}`}>
              {frontPhoto ? (
                <img src={frontPhoto} alt="Front of home" className="front-photo-preview" />
              ) : (
                <>
                  <div className="photo-icon">🏠</div>
                  <p>Capture the front of the home — this becomes the report hero.</p>
                </>
              )}
            </div>
            <PhotoSlot single photos={frontPhoto ? [frontPhoto] : []} onChange={(list) => setFrontPhoto(list[0] || '')} />
          </div>

          {/* HOMEOWNER */}
          <div className="inspection-section">
            <div className="section-label">HOMEOWNER</div>
            <div className="form-group">
              <input type="text" name="fullName" placeholder="Full name" value={formData.fullName} onChange={handleInputChange} className="form-input" />
            </div>
            <div className="form-group">
              <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} className="form-input" />
            </div>
            <div className="form-group">
              <input type="email" name="email" placeholder="Email (optional)" value={formData.email} onChange={handleInputChange} className="form-input" />
            </div>
          </div>

          {/* PROPERTY */}
          <div className="inspection-section">
            <div className="section-label">PROPERTY</div>
            <div className="form-group">
              <input type="text" name="streetAddress" placeholder="Street address" value={formData.streetAddress} onChange={handleInputChange} className="form-input" />
            </div>
          </div>

          {/* PROPERTY TYPE */}
          <div className="inspection-section">
            <div className="section-label">PROPERTY TYPE</div>
            <div className="property-type-grid">
              {PROPERTY_TYPES.map((type) => (
                <button
                  key={type}
                  className={`property-type-btn ${formData.propertyType === type ? 'selected' : ''}`}
                  onClick={() => setFormData((prev) => ({ ...prev, propertyType: type }))}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* CUSTOMER'S MAIN CONCERN */}
          <div className="inspection-section">
            <div className="section-label">CUSTOMER'S MAIN CONCERN</div>
            <textarea
              name="mainConcern"
              placeholder="What brings you out today?"
              value={formData.mainConcern}
              onChange={handleInputChange}
              className="form-textarea"
              rows="4"
            />
            <p className="form-help-text">In the customer's own words — kept on the follow-up for the sales call.</p>
          </div>
        </div>
      )}

      {/* SERVICES */}
      {activeTab === 'new' && currentStep === 1 && (
        <div className="inspection-content">
          <div className="services-intro">
            <h3>What are you inspecting today?</h3>
            <p>Attic is ready to inspect. The other services are coming next.</p>
          </div>

          <div className="services-grid">
            {SERVICES.map((service) => {
              const on = selectedServices.includes(service.id);
              return (
                <button
                  key={service.id}
                  className={`service-card ${on ? 'selected' : ''} ${service.soon ? 'soon' : ''}`}
                  onClick={() => !service.soon && handleToggleService(service.id)}
                  disabled={service.soon}
                >
                  <div className="service-card-left">
                    <div className="service-icon">{service.icon}</div>
                    <div className="service-info">
                      <h4>{service.name}</h4>
                      <p>{service.soon ? 'Coming soon' : service.description}</p>
                    </div>
                  </div>
                  <div className={`service-checkbox ${on ? 'checked' : ''}`}>{on && '✓'}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ATTIC INSPECTION */}
      {activeTab === 'new' && currentStep === 2 && (
        <AtticInspection
          key={atticKey}
          inspection={inspection}
          initialData={atticData}
          onChange={setAtticData}
          onBack={handleBackToPrevStep}
          onContinue={(data) => {
            setAtticData(data);
            setCurrentStep(3);
            handleInspectionDone(data);
          }}
        />
      )}

      {/* QUOTE BUILDER */}
      {activeTab === 'new' && currentStep === 3 && (
        <QuoteBuilder
          key={atticKey}
          inspection={{
            ...inspection,
            customerName: formData.fullName,
            serviceAddress: formData.streetAddress,
            email: formData.email,
            servicePhone: formData.phone,
            atticSize: totalAtticSqft(atticData?.atticAreas) || '', // every attic area added up
            zohoLinked: LEADS_SOURCE !== 'zoho' || !!linkedLeadId,
            heroPhoto: frontPhoto,
            photoCount: allPhotos.length,
            // what the customer will see, for the review page's View
            previewReport: ({ packages, formats }) =>
              buildInspectionReport({ lead: inspection, details: formData, frontPhoto, attic: atticData, packages, formats, preparer: CURRENT_USER.name }),
          }}
          initialState={quoteState}
          onStateChange={setQuoteState}
          photos={allPhotos}
          onSaveExit={handleSaveExit}
          onBack={handleBackToPrevStep}
          onContinue={handleSent}
        />
      )}

      {/* PREVIOUS REPORTS */}
      {activeTab === 'previous' && (
        <div className="inspection-content">
          <div className="previous-reports-header">
            <h3 className="previous-reports-title">Past inspections</h3>
            <p className="previous-reports-description">
              Resume a draft, resend the report to the homeowner, or review a finalized inspection.
            </p>
            {SHARED && (
              <div className={`shared-sync ${draftSync.status}`}>
                <span>
                  {draftSync.status === 'syncing' && '⟳ Loading everyone’s drafts…'}
                  {draftSync.status === 'ready' &&
                    `✓ Shared drafts · synced ${new Date(draftSync.syncedAt).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}`}
                  {draftSync.status === 'error' && `⚠ Couldn’t load shared drafts: ${draftSync.error}`}
                  {draftSync.status === 'idle' && 'Shared drafts'}
                </span>
                <button type="button" onClick={() => refreshDrafts()} disabled={draftSync.status === 'syncing'}>↻</button>
              </div>
            )}
          </div>

          <div className="previous-reports-search">
            <input
              type="search"
              placeholder="Search by name or address..."
              className="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="previous-reports-list">
            {sent.length === 0 && draftList.length === 0 && q && <p className="reports-empty">No reports match “{search}”.</p>}

            {/* SENT */}
            {sent.map(({ id, token, report, leadId, source, by }) => (
              <div className="inspection-report-card completed" key={id || token}>
                <div className="report-avatar completed">✓</div>
                <div className="report-info">
                  <h4 className="report-name">{report.header.customer || 'No name'}</h4>
                  <p className="report-address">{[report.header.address, report.header.cityline].filter(Boolean).join(', ') || 'No address'}</p>
                  <div className="report-status-row">
                    <span className="report-date">📅 {fmtDate(report.header.date)}</span>
                    <span className="report-status-badge generated">Sent{by ? ` · ${by}` : ''}</span>
                  </div>
                </div>
                <div className="report-actions">
                  {leadId && onOpenCommunication && (
                    <button className="btn-report-action primary" onClick={() => onOpenCommunication(leadId)}>✉️ Send</button>
                  )}
                  <button className="btn-report-action text-only" onClick={() => openSent({ id, report, leadId, source }, 'view')}>👁️ View</button>
                  {source && (
                    <button className="btn-report-action text-only" onClick={() => setEditChoice({ id, report, leadId, source })}>✏️ Edit</button>
                  )}
                </div>
              </div>
            ))}

            {/* DRAFTS — shared by every account, any device */}
            {draftList.map((d) => (
              <div className="inspection-report-card draft" key={d.id}>
                <div className="report-avatar draft">✏️</div>
                <div className="report-info">
                  <h4 className="report-name">{d.customer || 'No name yet'}</h4>
                  <p className="report-address">{d.address || 'No address yet'}</p>
                  <div className="report-status-row">
                    <span className="report-date">📅 {fmtDate(localDay(d.updatedAt))}</span>
                    <span className="report-status-badge draft">Draft · {['Details', 'Services', 'Inspection', 'Quote'][d.currentStep || 0]}</span>
                  </div>
                  <div className="report-edited">
                    Last edited {new Date(d.updatedAt).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    {d.editedBy ? ` by ${d.editedBy}` : ''}
                  </div>
                </div>
                <div className="report-actions">
                  <button
                    className="btn-report-action danger"
                    onClick={() => {
                      if (confirmDiscard === d.id) {
                        deleteDraft(d.id);
                        setConfirmDiscard(null);
                      } else {
                        setConfirmDiscard(d.id);
                      }
                    }}
                  >
                    {confirmDiscard === d.id ? 'Tap again to discard' : '✕ Discard'}
                  </button>
                  <button className="btn-report-action text-only" onClick={() => loadDraft(d)}>✏️ Resume</button>
                </div>
              </div>
            ))}

            {/* the blank report template */}
            {matches('Sample report', '') && (
              <div className="inspection-report-card completed">
                <div className="report-avatar completed">📄</div>
                <div className="report-info">
                  <h4 className="report-name">Sample report</h4>
                  <p className="report-address">The blank template every report is built from</p>
                </div>
                <div className="report-actions">
                  <button className="btn-report-action text-only" onClick={() => setViewingReport(getReport(DEMO_REPORT_TOKEN))}>👁️ View</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ACTION BUTTONS — the attic and quote pages have their own */}
      {activeTab === 'new' && currentStep < 2 && (
        <div className="inspection-actions">
          {currentStep === 0 && (
            <>
              <button className="btn-save-draft" onClick={handleSaveDraft}>Save draft</button>
              <button className="btn-continue" onClick={() => setCurrentStep(1)}>Continue →</button>
            </>
          )}
          {currentStep === 1 && (
            <>
              <button className="btn-back" onClick={handleBackToPrevStep}>← Back</button>
              <button className="btn-start-inspection" onClick={() => setCurrentStep(2)} disabled={!canStartInspection}>
                {canStartInspection ? 'Start inspection →' : 'Select Attic to start'}
              </button>
            </>
          )}
        </div>
      )}

      {/* Edit a sent inspection: fix the quote, or edit the report */}
      {editChoice && (
        <div className="edit-choice-overlay" onClick={() => setEditChoice(null)}>
          <div className="edit-choice" onClick={(e) => e.stopPropagation()}>
            <h3>Edit {editChoice.report?.header?.customer || 'this inspection'}</h3>
            <p>Resending replaces what was sent and updates the same quotes in Zoho.</p>
            <button type="button" className="edit-choice-btn" onClick={() => openSent(editChoice, 'quote')}>
              <b>💲 Fix the quote</b>
              <span>Open the saved packages — change services, prices or discounts</span>
            </button>
            <button type="button" className="edit-choice-btn" onClick={() => openSent(editChoice, 'report')}>
              <b>📝 Edit report</b>
              <span>Open the attic inspection — depth, findings and photos</span>
            </button>
            <button type="button" className="edit-choice-cancel" onClick={() => setEditChoice(null)}>Cancel</button>
          </div>
        </div>
      )}

      {toast && <div className="inspection-toast">{toast}</div>}
    </div>
  );
};

export default Inspection;
